import {
	AfterViewInit,
	Component,
	computed,
	DestroyRef,
	effect,
	ElementRef,
	input,
	InputSignal,
	OnInit,
	signal,
	Signal,
	ViewChild,
	WritableSignal
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { debounceTime, map } from 'rxjs/operators';

// models
import {
	IChatMessage,
	IChatTempMessage,
	IChatFileAttachment,
	IMentionableUsers,
	INewChatMessage,
	AllowedExtensions,
	AttachmentFile,
	StoreItem,
	ChatFormGroup,
	ChatFormValue,
	IChatMessageAttachmentPayload,
	IChatMessageAttachmentUpdate,
	FILE_SIZE_LIMIT,
	FILES_COUNT_LIMIT,
	IChatUser,
	AttachmentFileType,
	TYPING_REQUEST_DEBOUNCE_MS
} from '../../models/chat';
import { MixpanelEvents } from '../../models/mixpanelEvents';

// modals
import { IImagesViewModalData, ImagesViewModalComponent } from '../../modals/images-view-modal/images-view-modal.component';

// services
import { SHARED_MODULES } from '../../../shared.';
import { ChatService, FILE_EXTENSIONS_ALLOWED, IMAGE_EXTENSIONS, VIDEO_EXTENSIONS } from '../../services/chat.service';
import { MixpanelService } from '../../services/mixpanel.service';
import { SnackBarService } from '../../services/snack-bar.service';
import { Utils } from '../../services/utils.service';

// components
import { PhEmojiPanelComponent } from '../ph-emoji-panel/ph-emoji-panel.component';
import { PhIconWrapperComponent } from '../ph-icon-wrapper/ph-icon-wrapper.component';
import { PhIconComponent } from '../ph-icon/ph-icon.component';
import { LoaderComponent } from '../loader/loader.component';
import { PhChatMentionPickerComponent } from '../ph-chat-mention-picker/ph-chat-mention-picker.component';

// pipes
import { MentionParserPipe } from '../../pipes/mention-parser.pipe';

@Component({
	selector: 'ph-chat-input',
	templateUrl: './ph-chat-input.component.html',
	styleUrls: ['./ph-chat-input.component.scss'],
	imports: [
		...SHARED_MODULES,
		PhIconWrapperComponent,
		PhEmojiPanelComponent,
		PhIconComponent,
		LoaderComponent,
		MentionParserPipe,
		PhChatMentionPickerComponent
	]
})
export class PhChatInputComponent implements OnInit, AfterViewInit {

	private readonly formValueSignal: Signal<ChatFormValue>;
	public allowAttachments: InputSignal<boolean> = input<boolean>(true);
	public allowMentions: InputSignal<boolean> = input<boolean>(true);
	public allowEmoji: InputSignal<boolean> = input<boolean>(true);
	public maxLength: InputSignal<number> = input<number>(2000);
	public placeholderLabel: InputSignal<string> = input<string>('CHAT.WRITE_MESSAGE');
	public formGroup: FormGroup<ChatFormGroup>;
	public joinedAllowedExtensions: WritableSignal<string> = signal<string>('');
	public isDragging: WritableSignal<boolean> = signal<boolean>(false);
	public mentionedUsersIds: Set<number> = new Set<number>();
	public mentionQuery: WritableSignal<string> = signal<string>('');
	public repliedMessage: WritableSignal<IChatMessage> = signal<IChatMessage>(null);
	public showUserPicker: WritableSignal<boolean> = signal<boolean>(false);
	public editingMessage: WritableSignal<IChatMessage> = signal<IChatMessage>(null);

	private readonly typing$: Subject<void> = new Subject<void>();
	private readonly _mentionCheck$: Subject<void> = new Subject<void>();
	private _allStoresCache: Array<StoreItem> = [];
	private _allStoresSourceRef: Array<IMentionableUsers> | null = null;

	@ViewChild('textarea') textareaElement!: ElementRef<HTMLTextAreaElement>;
	@ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
	@ViewChild('mentionPicker') mentionPicker?: PhChatMentionPickerComponent;

	public allStores: Signal<Array<StoreItem>> = computed((): Array<StoreItem> => {
		if (!this.allowMentions()) return [];

		const users: Array<IMentionableUsers> = this.chatService.mentionableUsers();
		if (users === this._allStoresSourceRef && this._allStoresCache.length) {
			return this._allStoresCache;
		}

		const storesMap: Map<number, StoreItem> = new Map<number, StoreItem>();
		for (const user of users) {
			for (const plan of user.plans) {
				const existing: StoreItem | undefined = storesMap.get(plan.id);
				if (!existing) {
					storesMap.set(plan.id, { ...plan, users: [user] });
				} else {
					storesMap.set(plan.id, {
						...existing,
						users: [...existing.users, user]
					});
				}
			}
		}

		const result: Array<StoreItem> = Array.from(storesMap.values());
		this._allStoresSourceRef = users;
		this._allStoresCache = result;
		return result;
	});

	public filteredUsers: Signal<Array<IMentionableUsers>> = computed((): Array<IMentionableUsers> => {
		if (!this.allowMentions()) return [];
		const query: string = this.mentionQuery().toLowerCase();
		const users: Array<IMentionableUsers> = this.chatService.mentionableUsers();
		if (!query) return users;
		return users.filter((user: IMentionableUsers): boolean => user.full_name.toLowerCase().includes(query));
	});

	public filteredStores: Signal<Array<StoreItem>> = computed((): Array<StoreItem> => {
		const query: string = this.mentionQuery().toLowerCase();
		const stores: Array<StoreItem> = this.allStores();
		if (!query) return stores;
		return stores.filter((store: StoreItem): boolean => store.name.toLowerCase().includes(query));
	});

	public isSendDisabled: Signal<boolean> = computed((): boolean => {
		const { message, files } = this.formValueSignal();
		const content: string = message?.trim() || '';

		if (!this.allowAttachments()) return content.length === 0;

		const hasFiles: boolean = files?.length > 0;
		const isUploading: boolean = files?.some((f: AttachmentFile): boolean => f.loading);
		return (!content && !hasFiles) || isUploading;
	});

	constructor(
		private dialog: MatDialog,
		private formBuilder: FormBuilder,
		private chatService: ChatService,
		private destroyRef: DestroyRef,
		private snackBarService: SnackBarService,
		private mixpanelService: MixpanelService
	) {
		this.formGroup = this.formBuilder.group<ChatFormGroup>({
			message: this.formBuilder.control<string>('', [
				Validators.required,
				Validators.maxLength(this.maxLength()),
				(control: FormControl<string>): { empty: boolean } => {
					const value: string = control.value?.trim() || '';
					return value.length === 0 ? { empty: true } : null;
				}
			])
		});

		if (this.allowAttachments()) {
			this.formGroup.addControl('files', this.formBuilder.control<Array<AttachmentFile>>([]));
		}

		this.formValueSignal = toSignal(
			this.formGroup.valueChanges.pipe(
				map((value: Partial<ChatFormValue>): IChatTempMessage => {
					const result: IChatTempMessage = { message: value.message ?? '' };
					if (this.allowAttachments()) result.files = value.files || [];
					return result;
				})
			),
			{ initialValue: { message: '', files: [] } }
		);

		this._mentionCheck$
			.pipe(
				debounceTime(TYPING_REQUEST_DEBOUNCE_MS / 4),
				takeUntilDestroyed(this.destroyRef)
			)
			.subscribe((): void => this.checkMention());

		effect((): void => {
			this.chatService.tempMessage = {
				...this.formValueSignal(),
				replyMessage: this.repliedMessage(),
				editingMessage: this.editingMessage()
			};
		});

		if (this.chatService.tempMessage) {
			const temp: IChatTempMessage = this.chatService.tempMessage;
			if (temp.editingMessage) {
				this.editingMessage.set(temp.editingMessage);
				this.repliedMessage.set(null);
				this.formGroup.controls.message.setValue(temp.message);
				if (this.allowAttachments() && this.formGroup.controls.files) {
					this.formGroup.controls.files.setValue(temp.files || []);
				}
			} else {
				this.formGroup.controls.message.setValue(temp.message);
				if (this.formGroup.controls.files) {
					this.formGroup.controls.files.setValue(temp.files || []);
				}
				if (temp.replyMessage) {
					this.repliedMessage.set(temp.replyMessage);
				}
			}
		}

		this.typing$
			.pipe(
				debounceTime(TYPING_REQUEST_DEBOUNCE_MS),
				takeUntilDestroyed(this.destroyRef)
			)
			.subscribe((): void => {
				const content: string = this.formGroup.controls.message.value?.trim() || '';
				if (!content || !this.chatService.id) return;
				this.chatService.sendTyping(this.chatService.id)
					.pipe(takeUntilDestroyed(this.destroyRef))
					.subscribe();
			});
	}

	ngOnInit(): void {
		this.joinedAllowedExtensions.set(FILE_EXTENSIONS_ALLOWED.map((ext: string): string => `.${ext}`).join(','));

		this.chatService.replyMessage$
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((message: IChatMessage): void => {
				if (this.editingMessage()) {
					this.cancelEdit();
				}

				this.repliedMessage.set(message);

				const user: IMentionableUsers = this.chatService.mentionableUsers().find((u: IMentionableUsers): boolean => u.id === message.user?.id);
				if (user) {
					const currentContent: string = this.formGroup.controls.message.value || '';
					const mention = `@${user.full_name.trim()} `;
					this.formGroup.controls.message.setValue(mention + currentContent);
					this.mentionedUsersIds.add(user.id);
				}

				this.textareaElement.nativeElement.focus();
				this.setTextareaHeight();
			});

		this.chatService.editMessage$
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((message: IChatMessage): void => {
				if (message) this.startEdit(message);
			});
	}

	ngAfterViewInit(): void {
		this.setTextareaHeight();
		this.textareaElement.nativeElement.focus();

		this.destroyRef.onDestroy((): void => {
			const files: Array<AttachmentFile> = this.formGroup.controls.files?.value;
			const filtered: Array<AttachmentFile> = files?.length
				? files.filter((f: AttachmentFile): boolean => !f.loading)
				: [];
			this.chatService.tempMessage = { ...this.chatService.tempMessage, files: filtered };
		});
	}

	private isImage(file: File): boolean {
		return file.type.startsWith('image/');
	}

	private isVideo(file: File): boolean {
		return file.type.startsWith('video/');
	}

	private parseMentionsFromContent(content: string): string {
		if (!content) return '';
		const mentionRegex = /\[(@[^\]]+)]\(mention:(\d+)\)/g;
		return content.replace(mentionRegex, (_match: string, name: string, _id: string): string => name);
	}

	private startEdit(message: IChatMessage): void {
		if (!message) return;

		this.formGroup.controls.message.setValue('');
		if (this.allowAttachments() && this.formGroup.controls.files) {
			const currentFiles: Array<AttachmentFile> = this.formGroup.controls.files.value || [];
			currentFiles.forEach((f: AttachmentFile): void => {
				if (f.previewUrl) URL.revokeObjectURL(f.previewUrl);
				if (f.loading && f.subscription) f.subscription.unsubscribe();
			});
			this.formGroup.controls.files.setValue([]);
		}
		this.repliedMessage.set(null);
		this.mentionedUsersIds.clear();
		this.showUserPicker.set(false);

		this.editingMessage.set(message);
		const parsedContent: string = this.parseMentionsFromContent(message.content || '');
		this.formGroup.controls.message.setValue(parsedContent);

		if (this.allowAttachments() && this.formGroup.controls.files && message.attachments?.length) {
			const attachmentFiles: Array<AttachmentFile> = message.attachments
				.filter((a: IChatFileAttachment): string => a?.key && a?.name)
				.map((a: IChatFileAttachment): AttachmentFile => {
					const extension: string = (a.name.split('.').pop() || '').toLowerCase();
					const ct: string = a.content_type || '';
					const isImage: boolean = ct.startsWith('image/') || IMAGE_EXTENSIONS.includes(extension as AllowedExtensions);
					const isVideo: boolean = ct.startsWith('video/') || VIDEO_EXTENSIONS.includes(extension as AllowedExtensions);
					const type: AttachmentFileType = isImage ? 'image' : isVideo ? 'video' : 'file';
					return {
						file: null,
						loading: false,
						key: a.key,
						icon: this.chatService.getIconForFileExtension(extension),
						previewUrl: a.previewUrl || (isImage || isVideo ? a.file : undefined),
						name: a.name,
						type
					} as AttachmentFile;
				});
			if (attachmentFiles.length > 0) {
				this.formGroup.controls.files.setValue(attachmentFiles);
			}
		}

		this.mentionedUsersIds = message.mentioned_users?.length
			? new Set(message.mentioned_users.map((u: IChatUser): number => u.id))
			: new Set();

		setTimeout((): void => {
			this.textareaElement?.nativeElement?.focus();
			this.setTextareaHeight();
		});
	}

	protected cancelEdit(): void {
		this.editingMessage.set(null);

		this.formGroup.controls.message.setValue('');
		if (this.allowAttachments() && this.formGroup.controls.files) {
			const files: Array<AttachmentFile> = this.formGroup.controls.files.value || [];
			files.forEach((f: AttachmentFile): void => {
				if (f.previewUrl) URL.revokeObjectURL(f.previewUrl);
				if (f.loading && f.subscription) f.subscription.unsubscribe();
			});
			this.formGroup.controls.files.setValue([]);
		}

		this.repliedMessage.set(null);
		this.mentionedUsersIds.clear();
		this.showUserPicker.set(false);
		this.setTextareaHeight();
	}

	private findMentionStartIndex(): number {
		const value: string = this.formGroup.controls.message.value || '';
		const cursorPosition: number = this.textareaElement.nativeElement.selectionStart || 0;
		const textBeforeCursor: string = value.substring(0, cursorPosition);

		const lastAtIndex: number = textBeforeCursor.lastIndexOf('@');
		if (lastAtIndex === -1) return -1;
		const textAfterAt: string = textBeforeCursor.substring(lastAtIndex + 1);
		if (textAfterAt.endsWith(' ')) return -1;
		const spaceCount: number = (textAfterAt.match(/ /g) || []).length;
		if (spaceCount > 1) return -1;
		if (textAfterAt.includes('\n')) return -1;

		return lastAtIndex;
	}

	private processFiles(files: FileList | Array<File>): void {
		if (!files || !this.allowAttachments()) return;

		const overSizeFileNames: Array<string> = [];
		const notAllowedExtensionFileNames: Array<string> = [];

		let allowedFiles: Array<File> = Array.from(files).filter((file: File): boolean => {
			const extension: string = file.name.split('.').pop()?.toLowerCase();
			const isAllowedExtension: boolean = FILE_EXTENSIONS_ALLOWED.includes(extension);
			const isAllowedSize: boolean = file.size <= FILE_SIZE_LIMIT;

			if (!isAllowedExtension) notAllowedExtensionFileNames.push(file.name);
			if (!isAllowedSize) overSizeFileNames.push(file.name);
			return isAllowedExtension && isAllowedSize;
		});

		if (overSizeFileNames.length) {
			this.snackBarService.setSnackBarNotification({
				messageText: 'CHAT.FILES_OVER_SIZE',
				translateParams: { files: overSizeFileNames.join(', ') }
			});
		}
		if (notAllowedExtensionFileNames.length) {
			this.snackBarService.setSnackBarNotification({
				messageText: 'CHAT.FILES_NOT_ALLOWED_EXTENSION',
				translateParams: { files: notAllowedExtensionFileNames.join(', ') }
			});
		}

		const currentFilesCount: number = this.formGroup.controls.files?.value?.length || 0;
		if (currentFilesCount + allowedFiles.length > FILES_COUNT_LIMIT) {
			this.snackBarService.setSnackBarNotification({
				messageText: 'CHAT.FILES_COUNT_LIMIT_EXCEEDED'
			});
			allowedFiles = allowedFiles.slice(0, FILES_COUNT_LIMIT - currentFilesCount);
		}

		if (allowedFiles.length === 0) return;

		allowedFiles.forEach(async (file: File): Promise<void> => {
			let extension: string = file.name.split('.').pop()?.toLowerCase() || '';
			const type: AttachmentFileType = this.isImage(file) ? 'image' : this.isVideo(file)
				? 'video'
				: 'file';

			const fileItem: AttachmentFile = {
				file,
				loading: true,
				icon: this.chatService.getIconForFileExtension(extension),
				name: file.name,
				type
			};

			const currentFiles: Array<AttachmentFile> = this.formGroup.controls.files?.value || [];
			this.formGroup.controls.files?.setValue([...currentFiles, fileItem]);

			fileItem.previewUrl = 'loading';
			let fileToUpload: File = file;
			if (extension === 'heic') {
				fileToUpload = await Utils.heicToJpeg(file);
				fileItem.file = fileToUpload;
			}

			if (this.isImage(fileToUpload)) {
				fileToUpload = await Utils.resizeImage(fileToUpload);
				fileItem.file = fileToUpload;
				fileItem.previewUrl = URL.createObjectURL(fileToUpload);
			} else if (this.isVideo(fileToUpload)) {
				fileItem.previewUrl = URL.createObjectURL(fileToUpload);
			}

			fileItem.subscription = this.chatService.uploadFile(fileToUpload)
				.pipe(takeUntilDestroyed(this.destroyRef))
				.subscribe({
					next: (response: { key: string, file: string }): void => {
						const updated: Array<AttachmentFile> = [...(this.formGroup.controls.files?.value || [])];
						const index: number = updated.indexOf(fileItem);
						if (index !== -1) {
							updated[index] = { ...fileItem, loading: false, key: response.key, link: response.file };
						}
						this.formGroup.controls.files?.setValue(updated);
					},
					error: (): void => {
						const updated: Array<AttachmentFile> = [...(this.formGroup.controls.files?.value || [])];
						const index: number = updated.indexOf(fileItem);
						if (index !== -1) {
							updated.splice(index, 1);
						}
						this.formGroup.controls.files?.setValue(updated);
						this.snackBarService.setSnackBarNotification({
							messageText: 'CHAT.FAILED_FILE_ATTACH',
							translateParams: { file_name: file.name }
						});
					}
				});
		});

		this.setTextareaHeight();
	}

	private setTextareaHeight(): void {
		this.chatService.inputMessage$.next();
		const textarea: HTMLTextAreaElement = this.textareaElement.nativeElement;
		textarea.style.height = 'auto';
		textarea.style.height = `${textarea.scrollHeight}px`;
	}

	private closeMentionPickerAndFocusAt(caretPosition: number): void {
		this.showUserPicker.set(false);
		setTimeout((): void => {
			this.textareaElement.nativeElement.focus();
			this.textareaElement.nativeElement.setSelectionRange(caretPosition, caretPosition);
			this.setTextareaHeight();
		});
	}

	protected addMentionSymbol(): void {
		if (!this.allowMentions()) return;

		const textarea: HTMLTextAreaElement = this.textareaElement.nativeElement;
		const value: string = this.formGroup.controls.message.value || '';
		const cursorPosition: number = textarea.selectionStart || 0;

		const newValue: string = value.substring(0, cursorPosition) + '@' + value.substring(cursorPosition);
		this.formGroup.controls.message.setValue(newValue);

		const newCursorPos: number = cursorPosition + 1;

		setTimeout((): void => {
			textarea.focus();
			textarea.setSelectionRange(newCursorPos, newCursorPos);
			this.onInput();
		});
	}

	protected checkMention(): void {
		if (!this.allowMentions()) return;

		const atIndex: number = this.findMentionStartIndex();

		if (atIndex === -1) {
			this.showUserPicker.set(false);
			return;
		}

		const query: string = (this.formGroup.controls.message.value || '').substring(
			atIndex + 1,
			this.textareaElement.nativeElement.selectionStart
		);
		if (this.mentionQuery() !== query) {
			this.mentionQuery.set(query);
		}

		const hasUsers: boolean = this.filteredUsers().length > 0;
		const hasGroups: boolean = this.filteredStores().length > 0;
		this.showUserPicker.set(hasUsers || hasGroups);
	}

	protected onInput(): void {
		this.setTextareaHeight();
		this._mentionCheck$.next();
		this.typing$.next();
	}

	protected onFileSelected(event: any): void {
		if (!this.allowAttachments()) return;
		const files: FileList = event.target.files;
		this.processFiles(files);
		this.fileInput.nativeElement.value = '';
	}

	protected onDragOver(event: DragEvent): void {
		if (!this.allowAttachments()) return;
		event.preventDefault();
		event.stopPropagation();
		this.isDragging.set(true);
	}

	protected onDragLeave(event: DragEvent): void {
		if (!this.allowAttachments()) return;
		event.preventDefault();
		event.stopPropagation();
		this.isDragging.set(false);
	}

	protected onDrop(event: DragEvent): void {
		if (!this.allowAttachments()) return;
		event.preventDefault();
		event.stopPropagation();
		this.isDragging.set(false);

		const files: FileList = event.dataTransfer?.files;
		if (files) {
			this.processFiles(files);
		}
	}

	protected onKeyDown(event: KeyboardEvent): void {
		const handledByMentionPicker: boolean = !!this.allowMentions()
			&& !!this.showUserPicker()
			&& !!this.mentionPicker
			&& this.mentionPicker.handleKeyDown(event);

		if (handledByMentionPicker) {
			return;
		}

		if (event.key === 'Enter' && !event.shiftKey) {
			event.preventDefault();
			void this.sendMessage();
		}
	}

	protected onKeyUp(event: KeyboardEvent): void {
		if (event.key === 'Escape') return;
		this._mentionCheck$.next();
	}

	protected onPaste(event: ClipboardEvent): void {
		if (!this.allowAttachments()) return;
		const items: DataTransferItemList = event.clipboardData?.items;
		if (items) {
			const files: Array<File> = [];
			for (let i: number = 0; i < items.length; i++) {
				if (items[i].kind === 'file') {
					const file: File = items[i].getAsFile();
					if (file) files.push(file);
				}
			}
			if (files.length > 0) {
				event.preventDefault();
				this.processFiles(files);
			}
		}
	}

	protected openImage(file: AttachmentFile): void {
		if (file.loading) return;
		const files: Array<AttachmentFile> = (this.formGroup.controls.files?.value || []).filter((f: AttachmentFile): boolean => f.previewUrl && !f.loading);

		this.dialog
			.open<ImagesViewModalComponent, IImagesViewModalData>(ImagesViewModalComponent, {
				width: 'auto',
				autoFocus: false,
				backdropClass: 'back-drop',
				data: {
					images: files.map((f: AttachmentFile): string => f.previewUrl as string),
					types: files.map((f: AttachmentFile): 'image' | 'video' => f.type === 'video' ? 'video' : 'image'),
					names: files.map((f: AttachmentFile): string => f.file?.name || f.name),
					index: files.indexOf(file),
					showMainButton: false,
					showSecondaryButton: false
				}
			})
			.afterClosed().subscribe();
	}

	protected removeFile(index: number): void {
		const updated: Array<AttachmentFile> = [...(this.formGroup.controls.files?.value || [])];
		const [removed] = updated.splice(index, 1);
		if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);
		if (removed?.loading && removed.subscription) removed.subscription.unsubscribe();
		if (removed?.key) this.chatService.deleteFile(removed.key).subscribe();
		this.formGroup.controls.files.setValue(updated);
	}

	protected scrollToReplyMessage(): void {
		this.chatService.scrollToMessage$.next(this.repliedMessage().id as number);
	}

	protected selectUser(user: IMentionableUsers): void {
		if (!this.allowMentions()) return;

		const value: string = this.formGroup.controls.message.value || '';
		const cursorPosition: number = this.textareaElement.nativeElement.selectionStart || 0;
		const textAfterCursor: string = value.substring(cursorPosition);
		const atIndex: number = this.findMentionStartIndex();

		if (atIndex === -1) return;

		const textBeforeAt: string = value.substring(0, atIndex);
		const newTextBeforeCursor: string = textBeforeAt + `@${user.full_name.trim()} `;

		this.formGroup.controls.message.setValue(newTextBeforeCursor + textAfterCursor);
		this.mentionedUsersIds.add(user.id);
		this.closeMentionPickerAndFocusAt(newTextBeforeCursor.length);
	}

	protected selectStore(store: StoreItem): void {
		if (!this.allowMentions()) return;

		const value: string = this.formGroup.controls.message.value || '';
		const cursorPosition: number = this.textareaElement.nativeElement.selectionStart || 0;
		const textAfterCursor: string = value.substring(cursorPosition);
		const atIndex: number = this.findMentionStartIndex();

		if (atIndex === -1) return;

		const usersInStore: Array<IMentionableUsers> = store.users;
		if (usersInStore.length === 0) return;

		const mentionsString: string = usersInStore.map((u: IMentionableUsers): string => `@${u.full_name.trim()}`).join(
			' ') + ' ';
		const textBeforeAt: string = value.substring(0, atIndex);
		const newTextBeforeCursor: string = textBeforeAt + mentionsString;

		this.formGroup.controls.message.setValue(newTextBeforeCursor + textAfterCursor);

		usersInStore.forEach((u: IMentionableUsers): Set<number> => this.mentionedUsersIds.add(u.id));

		this.closeMentionPickerAndFocusAt(newTextBeforeCursor.length);
	}

	protected onMentionUserSelected(user: IMentionableUsers): void {
		this.selectUser(user);
	}

	protected onMentionStoreSelected(store: StoreItem): void {
		this.selectStore(store);
	}

	protected onMentionPickerClosed(): void {
		this.showUserPicker.set(false);
		this.mentionQuery.set('');
	}

	protected cancelReply(): void {
		this.formGroup.controls.message.setValue('');
		this.repliedMessage.set(null);
		this.setTextareaHeight();
	}

	protected async sendMessage(): Promise<void> {
		if (this.isSendDisabled()) return;

		const editingMsg: IChatMessage = this.editingMessage();
		const hasContent: boolean = !!this.formGroup.controls.message.value?.trim();
		let hasUploadedFiles: boolean = false;
		let uploadedFiles: Array<AttachmentFile> = [];

		if (this.allowAttachments()) {
			const files: Array<AttachmentFile> = this.formGroup.controls.files?.value || [];
			uploadedFiles = files.filter((f: AttachmentFile): string => !f.loading && f.key);
			hasUploadedFiles = uploadedFiles.length > 0;

			if (files?.length) {
				files.forEach((f: AttachmentFile): void => {
					if (f.loading && f.subscription) f.subscription.unsubscribe();
				});
			}
		}

		if (!hasContent && !hasUploadedFiles) return;

		let content: string = this.formGroup.controls.message.value?.trim() || '';
		const message: INewChatMessage = { content };

		if (this.repliedMessage()) {
			message.reply_to = this.repliedMessage()?.id as number;
			message.localData = {
				replyContent: this.repliedMessage()?.content,
				replyUser: this.repliedMessage()?.user
			};
		}

		if (this.allowMentions() && content) {
			const users: Array<IMentionableUsers> = this.chatService.mentionableUsers();
			if (users.length) {
				const sortedUsers: Array<IMentionableUsers> = [...users].sort((a: IMentionableUsers, b: IMentionableUsers): number => b.full_name.length - a.full_name.length);
				const escapedNames: Array<string> = sortedUsers.map((u: IMentionableUsers): string => u.full_name.trim().replace(
					/[.*+?^${}()|[\]\\]/g,
					'\\$&'
				));

				const regex = new RegExp(`@(${escapedNames.join('|')})(?![a-zA-Z0-9])`, 'g');
				const finalMentionedIds: Set<number> = new Set();

				content = content.replace(
					regex,
					(match: string, name: string, offset: number, fullString: string): string => {
						const before: string = fullString.substring(0, offset);
						const after: string = fullString.substring(offset + match.length);

						if (before.endsWith('[') && after.startsWith('](mention:')) {
							const idMatch: RegExpMatchArray = after.match(/^]\(mention:(\d+)\)/);
							if (idMatch) finalMentionedIds.add(Number(idMatch[1]));
							return match;
						}

						const user: IMentionableUsers = sortedUsers.find((u: IMentionableUsers): boolean => u.full_name.trim() === name && this.mentionedUsersIds.has(
							u.id));

						if (user) {
							finalMentionedIds.add(user.id);
							return `[${match}](mention:${user.id})`;
						}
						return match;
					}
				);

				message.content = content;
				message.mentioned_users = Array.from(finalMentionedIds);
			}
		}

		if (hasUploadedFiles) {
			message.attachments = uploadedFiles.map((f: AttachmentFile): IChatMessageAttachmentPayload => ({
				key: f.key as string,
				file: f.link as string,
				name: f.file?.name || f.name || ''
			}));
		}

		if (editingMsg && typeof editingMsg.id === 'number') {
			const attachList: Array<AttachmentFile> = (this.formGroup.controls.files?.value || []).filter((f: AttachmentFile): string => !f.loading && f.key);
			const updatePayload: Record<string, unknown> = {
				content: message.content,
				attachments: attachList.map((f: AttachmentFile): IChatMessageAttachmentUpdate => ({
					key: f.key!,
					name: f.name ?? '',
					file: f.previewUrl ?? '',
					content_type: f.file?.type ?? '',
					icon: f.icon
				}))
			};
			if (message.mentioned_users?.length) {
				updatePayload.mentioned_users = message.mentioned_users;
			}
			this.cancelEdit();
			this.chatService.updateMessageRequest$.next({
				message: editingMsg,
				payload: updatePayload
			});
			return;
		}

		this.mixpanelService.trackPage(MixpanelEvents.MessageSent, {
			has_attachment: hasUploadedFiles,
			attachment_type: uploadedFiles?.map((f: AttachmentFile): string => f.file?.type).join('; ')
		});

		this.chatService.newMessage$.next(message);
		this.formGroup.controls.message.setValue('');
		if (this.allowAttachments()) {
			this.formGroup.controls.files.value?.forEach((f: AttachmentFile): void => {
				if (f.previewUrl) URL.revokeObjectURL(f.previewUrl);
			});
			this.formGroup.controls.files.setValue([]);
		}
		this.mentionedUsersIds.clear();
		this.showUserPicker.set(false);
		this.repliedMessage.set(null);
		this.chatService.tempMessage = { message: '', files: [] };
		this.textareaElement.nativeElement.focus();
		this.setTextareaHeight();
	}

	insertEmoji(emoji: string): void {
		if (!emoji) return;

		const control: FormControl<string> = this.formGroup.controls.message;
		const textarea: HTMLTextAreaElement = this.textareaElement.nativeElement;
		const currentValue: string = control.value ?? '';

		const selectionStart: number = textarea.selectionStart ?? currentValue.length;
		const selectionEnd: number = textarea.selectionEnd ?? currentValue.length;

		const nextValue: string =
			currentValue.slice(0, selectionStart) +
			emoji +
			currentValue.slice(selectionEnd);

		control.setValue(nextValue);

		setTimeout((): void => {
			const caretPosition: number = selectionStart + emoji.length;
			textarea.focus();
			textarea.setSelectionRange(caretPosition, caretPosition);
			this.setTextareaHeight();
		});
	}
}